import React, { useEffect } from "react";

import { css, cx } from "@emotion/css";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useScroll } from "@/hooks/useScroll.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { InsulinDataDisplayType } from "@/models/DiabetesDisplayModeModel.ts";
import { Patient } from "@/models/PatientModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Loader } from "@/uiKit/atoms/Loader.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { BackToTop } from "@/uiKit/molecules/BackToTop.tsx";
import { DailyGraphContent } from "@/uiKit/organisms/DailyGraphList/DailyGraphContent.tsx";
import { DateUtils } from "@/utils/dateUtils.ts";

export type DailyGraphListProps = {
  patient: Patient;
  to: DateTime;
  insulinDisplayMode: InsulinDataDisplayType;
  className?: string;
  isDexcom: boolean;
};

/**
 * The list of daily graphs for a patient.
 * Infinitely scrollable.
 * The list is sorted by date, from the most recent to the oldest.
 *
 * @param patient
 * @param propsTo
 * @param insulinDisplayMode
 * @param className
 * @constructor
 */
export const DailyGraphList: React.FC<DailyGraphListProps> = ({
  patient,
  to: propsTo,
  insulinDisplayMode,
  className,
  isDexcom,
}) => {
  // Check if the end date (to) is yesterday
  const addTodayData = DateUtils.compareDates(propsTo, DateTime.now()) === -1;
  const from = propsTo.minus({ days: 30 });
  const to = addTodayData ? propsTo.plus({ day: 1 }) : propsTo;

  const scroll = useScroll();
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const dataviz = Queries.diabetes.useInfiniteCGMDailyGraph({
    patientId: patient.id,
    from: from,
    to: to,
  });

  useEffect(() => {
    if (scroll.isNearBottom && !dataviz.isFetching) {
      dataviz.fetchNextPage({ cancelRefetch: false });
    }
  });

  return (
    <div className={cx(styles.container, className)}>
      <BackToTop id="daily-graph-back-to-top" />
      <Card>
        <CardHeader
          title={
            <>
              <Typography variant="h5">{t("pages.patientMonitoring.glycemia.title")}</Typography>
              {isDexcom && <Typography variant="body">{t("pages.addMeasurementFile.dexcom.dexcomData")}</Typography>}
            </>
          }
        />
        <CardContent>
          <div className={styles.list}>
            {dataviz.hasPreviousPage ? (
              <Button
                data-action="monitoring.daily-graph.load-more"
                variant="outlined"
                startIcon={<Icons.chevronUp className={styles.moreContentIcon} />}
                className={styles.newerDataButton}
                onClick={() => dataviz.fetchPreviousPage()}
              >
                {t("pages.patientMonitoring.loadMoreRecent")}
              </Button>
            ) : null}
            {dataviz.data?.pages
              .flatMap(a => a)
              .map(data => (
                <DailyGraphContent
                  key={data.date}
                  data={data}
                  insulinDisplayMode={insulinDisplayMode}
                  isDexcom={isDexcom}
                />
              ))}
            {dataviz.hasNextPage ? (
              <div className={styles.loader}>
                <Loader size="M" />
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
  `,

  list: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `,
  newerDataButton: css`
    width: fit-content;
    color: ${theme.palette.text.primary};
  `,
  moreContentButton: css`
    width: fit-content;
    color: ${theme.palette.common.white};
  `,
  moreContentIcon: css`
    width: ${FontSizes.bodyLarge};
    height: ${FontSizes.bodyLarge};
  `,
  loader: css`
    width: 100%;
    display: flex;
    justify-content: center;
  `,
});
